import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import InfoCard from '../../components/shared/InfoCard'
import { formatOptionValue } from '../../utils/formatters'
import { supabase } from '../../lib/supabase'

interface MarketSettings {
  market?: string
  model_id: number
  production_start: number | null
  production_end: number | null
  maintenance_cost: number | null
  fleet_avg_aftt: number | null
  use_manufacturing_date: boolean
}

interface ABBValue {
  year_model: number
  year: number
  quarter: number
  value: number | null
}

interface MarketSettingsSectionProps {
  modelId: string
}

export default function MarketSettingsSection({ modelId }: MarketSettingsSectionProps) {
  const navigate = useNavigate()
  const [marketSettings, setMarketSettings] = useState<MarketSettings | null>(null)
  const [savingSettings, setSavingSettings] = useState(false)
  const [abbValues, setAbbValues] = useState<ABBValue[]>([])
  const [loadingValues, setLoadingValues] = useState(false)
  const [selectedYearModel, setSelectedYearModel] = useState<number | null>(null)
  const [yearModels, setYearModels] = useState<number[]>([])

  useEffect(() => {
    fetchMarketSettings()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId])

  const fetchMarketSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('market_settings')
        .select('*')
        .eq('model_id', parseInt(modelId, 10))
        .maybeSingle()

      if (error && error.code !== 'PGRST116') {
        throw error
      }

      if (data) {
        setMarketSettings({
          ...data,
          use_manufacturing_date: data.use_manufacturing_date ?? false
        })
        fetchCurrentValues(data)
      } else {
        setMarketSettings({
          model_id: parseInt(modelId, 10),
          production_start: null,
          production_end: null,
          maintenance_cost: null,
          fleet_avg_aftt: null,
          use_manufacturing_date: false
        })
      }
    } catch (err: any) {
      // Error fetching market settings
    }
  }

  const fetchCurrentValues = async (settings: MarketSettings) => {
    if (!settings.production_start) return

    try {
      setLoadingValues(true)
      const endYear = settings.production_end || new Date().getFullYear()
      const models = Array.from(
        { length: endYear - settings.production_start + 1 },
        (_, i) => settings.production_start! + i
      )
      setYearModels(models)

      if (!selectedYearModel && models.length > 0) {
        setSelectedYearModel(models[0])
      }

      const { data, error } = await supabase
        .from('abb')
        .select('*')
        .eq('model_id', parseInt(modelId, 10))
        .order('year_model', { ascending: true })
        .order('year', { ascending: true })
        .order('quarter', { ascending: true })

      if (error) throw error
      setAbbValues(data || [])
    } catch (err: any) {
      // Error fetching ABB values
    } finally {
      setLoadingValues(false)
    }
  }

  const getValuesForYearModel = () => {
    if (!selectedYearModel || !marketSettings) return []
    const currentYear = new Date().getFullYear()
    const rows = []

    for (let year = selectedYearModel; year <= currentYear; year++) {
      const yearData = abbValues.filter(
        v => v.year_model === selectedYearModel && v.year === year
      )
      rows.push({
        year,
        q1: yearData.find(d => d.quarter === 1)?.value || null,
        q2: yearData.find(d => d.quarter === 2)?.value || null,
        q3: yearData.find(d => d.quarter === 3)?.value || null,
        q4: yearData.find(d => d.quarter === 4)?.value || null
      })
    }
    return rows
  }

  const handleSaveMarketSettings = async () => {
    if (!marketSettings) return

    try {
      setSavingSettings(true)
      const settingsData = {
        model_id: marketSettings.model_id,
        production_start: marketSettings.production_start,
        production_end: marketSettings.production_end,
        maintenance_cost: marketSettings.maintenance_cost,
        fleet_avg_aftt: marketSettings.fleet_avg_aftt,
        use_manufacturing_date: marketSettings.use_manufacturing_date
      }

      if (marketSettings.market) {
        const { error } = await supabase
          .from('market_settings')
          .update(settingsData)
          .eq('market', marketSettings.market)
        if (error) throw error
      } else {
        const { data, error } = await supabase
          .from('market_settings')
          .insert(settingsData)
          .select()
          .single()
        if (error) throw error
        if (data) {
          setMarketSettings({ ...marketSettings, market: data.market })
        }
      }

      alert('Market settings saved successfully!')
      fetchCurrentValues(marketSettings)
    } catch (err: any) {
      alert('Failed to save market settings: ' + err.message)
    } finally {
      setSavingSettings(false)
    }
  }

  return (
    <>
      <InfoCard
        title="Market Parameters"
        action={
          <button
            onClick={handleSaveMarketSettings}
            disabled={savingSettings || !marketSettings}
            className="bg-purple-200 text-purple-950 hover:bg-purple-300 px-3 py-1 rounded text-sm font-medium transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {savingSettings ? 'Saving...' : 'Save'}
          </button>
        }
      >
        <div className="space-y-4">
          {marketSettings ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Production Start Year</label>
                <input
                  type="number"
                  value={marketSettings.production_start || ''}
                  onChange={(e) => setMarketSettings({
                    ...marketSettings,
                    production_start: e.target.value ? parseInt(e.target.value, 10) : null
                  })}
                  placeholder="e.g., 2010"
                  className="block w-full px-3 py-2 text-gray-900 border border-gray-300 rounded-md shadow-sm placeholder:text-gray-400 focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Production End Year</label>
                <input
                  type="number"
                  value={marketSettings.production_end || ''}
                  onChange={(e) => setMarketSettings({
                    ...marketSettings,
                    production_end: e.target.value ? parseInt(e.target.value, 10) : null
                  })}
                  placeholder="e.g., 2025"
                  className="block w-full px-3 py-2 text-gray-900 border border-gray-300 rounded-md shadow-sm placeholder:text-gray-400 focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Maintenance Cost</label>
                <input
                  type="number"
                  value={marketSettings.maintenance_cost || ''}
                  onChange={(e) => setMarketSettings({
                    ...marketSettings,
                    maintenance_cost: e.target.value ? parseInt(e.target.value, 10) : null
                  })}
                  placeholder="e.g., 500000"
                  className="block w-full px-3 py-2 text-gray-900 border border-gray-300 rounded-md shadow-sm placeholder:text-gray-400 focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fleet Average AFTT</label>
                <input
                  type="number"
                  value={marketSettings.fleet_avg_aftt || ''}
                  onChange={(e) => setMarketSettings({
                    ...marketSettings,
                    fleet_avg_aftt: e.target.value ? parseInt(e.target.value, 10) : null
                  })}
                  placeholder="e.g., 3500"
                  className="block w-full px-3 py-2 text-gray-900 border border-gray-300 rounded-md shadow-sm placeholder:text-gray-400 focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                />
              </div>
              <div className="pt-2 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Relative Value Date Source</label>
                    <p className="text-xs text-gray-500 mt-1">
                      Choose which date to use for ABB (Average Blue Book) lookups
                    </p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <button
                      type="button"
                      onClick={() => setMarketSettings({
                        ...marketSettings,
                        use_manufacturing_date: false
                      })}
                      className={`px-3 py-1.5 text-sm font-medium rounded-l-md border transition-colors duration-150 ${
                        !marketSettings.use_manufacturing_date
                          ? 'bg-purple-950 text-white border-purple-950'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Delivery Date
                    </button>
                    <button
                      type="button"
                      onClick={() => setMarketSettings({
                        ...marketSettings,
                        use_manufacturing_date: true
                      })}
                      className={`px-3 py-1.5 text-sm font-medium rounded-r-md border -ml-3 transition-colors duration-150 ${
                        marketSettings.use_manufacturing_date
                          ? 'bg-purple-950 text-white border-purple-950'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Manufacturing Date
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-500 italic">Loading market parameters...</p>
          )}
        </div>
      </InfoCard>

      <InfoCard
        title="Value Tracking"
        action={
          marketSettings?.production_start ? (
            <button
              onClick={() => navigate(`/market/${modelId}/configure/abb`)}
              className="bg-purple-200 text-purple-950 hover:bg-purple-300 px-3 py-1 rounded text-sm font-medium transition-colors duration-150"
            >
              Configure Values
            </button>
          ) : null
        }
      >
        <div className="space-y-4">
          {!marketSettings?.production_start ? (
            <p className="text-sm text-gray-500 italic">
              Please set the Production Start Year in Market Parameters before configuring values.
            </p>
          ) : loadingValues ? (
            <p className="text-sm text-gray-500 italic">Loading values...</p>
          ) : (
            <>
              {/* Year Model Pills */}
              <div className="flex flex-wrap gap-2">
                {yearModels.map((yearModel) => (
                  <button
                    key={yearModel}
                    onClick={() => setSelectedYearModel(yearModel)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-150 ${
                      selectedYearModel === yearModel
                        ? 'bg-purple-950 text-white'
                        : 'bg-purple-100 text-purple-800 hover:bg-purple-200'
                    }`}
                  >
                    {yearModel}
                  </button>
                ))}
              </div>

              {/* Values Table */}
              {selectedYearModel && (
                <div className="mt-4">
                  <div className="overflow-hidden rounded-lg border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-green-800">
                        <tr>
                          <th className="px-3 py-1.5 text-left text-xs font-medium text-white uppercase tracking-wider">Year</th>
                          <th className="px-3 py-1.5 text-right text-xs font-medium text-white uppercase tracking-wider">Q1</th>
                          <th className="px-3 py-1.5 text-right text-xs font-medium text-white uppercase tracking-wider">Q2</th>
                          <th className="px-3 py-1.5 text-right text-xs font-medium text-white uppercase tracking-wider">Q3</th>
                          <th className="px-3 py-1.5 text-right text-xs font-medium text-white uppercase tracking-wider">Q4</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {getValuesForYearModel().map((row) => (
                          <tr key={row.year} className="hover:bg-green-50">
                            <td className="px-3 py-1.5 whitespace-nowrap text-sm font-medium text-gray-900">{row.year}</td>
                            <td className="px-3 py-1.5 whitespace-nowrap text-sm text-gray-900 text-right">
                              {row.q1 !== null ? formatOptionValue(row.q1) : '-'}
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap text-sm text-gray-900 text-right">
                              {row.q2 !== null ? formatOptionValue(row.q2) : '-'}
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap text-sm text-gray-900 text-right">
                              {row.q3 !== null ? formatOptionValue(row.q3) : '-'}
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap text-sm text-gray-900 text-right">
                              {row.q4 !== null ? formatOptionValue(row.q4) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Currently viewing: {selectedYearModel} Year Model
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </InfoCard>
    </>
  )
}

